import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import WidgetFooter from '../../components/WidgetFooter';
import { toast } from 'sonner';
import { 
  Leaf, ChevronRight, ChevronLeft, Check, User, Briefcase, 
  Calendar, Calculator, Send, Loader2, Info, MapPin, Mail,
  Phone, Clock, Euro, ExternalLink, CalendarDays, Plus, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { widgetApi } from '../../lib/api';
import { getImageUrl } from '../../lib/imageUtils';
import { format, addDays, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isAfter, isBefore, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';

const STEPS = [
  { id: 'services', title: 'Prestations', icon: Briefcase },
  { id: 'planning', title: 'Planning', icon: Calendar },
  { id: 'contact', title: 'Coordonnées', icon: User },
  { id: 'summary', title: 'Récapitulatif', icon: Calculator },
];

// Duration options
const DURATION_OPTIONS = [
  { value: 'half_day', label: '½ journée', hours: 3.5 },
  { value: 'full_day', label: 'Journée complète', hours: 7 },
];

// Default gardening/landscaping services for personal services
const DEFAULT_SERVICES = [
  { id: 'tonte', label: 'Tonte de pelouse', icon: '🌿' },
  { id: 'taille_haies', label: 'Taille de haies', icon: '✂️' },
  { id: 'desherbage', label: 'Désherbage', icon: '🌱' },
  { id: 'ramassage_feuilles', label: 'Ramassage de feuilles', icon: '🍂' },
  { id: 'taille_arbustes', label: 'Taille d\'arbustes', icon: '🌳' },
  { id: 'entretien_massifs', label: 'Entretien des massifs', icon: '🌺' },
  { id: 'debroussaillage', label: 'Débroussaillage', icon: '🪓' },
  { id: 'arrosage', label: 'Arrosage', icon: '💧' },
  { id: 'scarification', label: 'Scarification', icon: '🔧' },
  { id: 'evacuation_dechets', label: 'Évacuation des déchets verts', icon: '🚛' },
  { id: 'autre', label: 'Autre (préciser)', icon: '📝' },
];

export default function SubscriptionWidget() {
  const { widgetId } = useParams();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [config, setConfig] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [availability, setAvailability] = useState([]);
  const [closures, setClosures] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Custom theme from widget config or URL params
  const primaryColor = widgetConfig?.primary_color || searchParams.get('primary') || '#8BC34A';
  const secondaryColor = widgetConfig?.secondary_color || searchParams.get('secondary') || '#4CAF50';
  const textColor = widgetConfig?.text_color || searchParams.get('text') || '#1a1a1a';
  const bgColor = widgetConfig?.bg_color || '#F9F8F6';

  // Form data
  const [formData, setFormData] = useState({
    selected_services: [],
    other_service_description: '',
    needs_description: '',
    slot_duration: 'half_day',
    selected_slots: [],
    client_name: '',
    client_email: '',
    client_phone: '',
    client_address: '',
    urssaf_email: '',
    notes: '',
  });

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await widgetApi.getSubscriptionConfig(widgetId);
        setConfig(response.data);
        setWidgetConfig(response.data.widget);
        setAvailability(response.data.availability || []);
        setClosures(response.data.closures || []);
      } catch (error) {
        console.error('Error loading widget config:', error);
        toast.error('Erreur lors du chargement du widget');
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, [widgetId]);

  const hourlyRate = config?.hourly_rate || config?.company?.default_hourly_rate || 35;
  const hoursPerSlot = formData.slot_duration === 'full_day' ? 7 : 3.5;

  // Calculate pricing
  const calculatePricing = () => {
    const numSlots = formData.selected_slots.length;
    if (numSlots === 0) return { perVisit: 0, total: 0, frequency: '', urssafTotal: 0 };

    const pricePerVisit = hoursPerSlot * hourlyRate;
    const totalPrice = pricePerVisit * numSlots;
    const frequency = numSlots >= 4 ? 'mensuel' : numSlots >= 2 ? 'bi-mensuel' : 'ponctuel';
    const urssafPrice = totalPrice / 2; // 50% tax credit

    return {
      perVisit: pricePerVisit,
      total: totalPrice,
      frequency,
      urssafTotal: urssafPrice,
      numSlots,
      hoursPerSlot
    };
  };

  const handleServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      selected_services: prev.selected_services.includes(serviceId)
        ? prev.selected_services.filter(s => s !== serviceId)
        : [...prev.selected_services, serviceId]
    }));
  };

  const handleSlotToggle = (date, period) => {
    setFormData(prev => {
      const existingSlot = prev.selected_slots.find(
        s => isSameDay(new Date(s.date), date) && s.period === period
      );
      if (existingSlot) {
        return {
          ...prev,
          selected_slots: prev.selected_slots.filter(
            s => !(isSameDay(new Date(s.date), date) && s.period === period)
          )
        };
      }
      return {
        ...prev,
        selected_slots: [...prev.selected_slots, { date: date.toISOString(), period }]
      };
    });
  };

  const isSlotSelected = (date, period) => {
    return formData.selected_slots.some(
      s => isSameDay(new Date(s.date), date) && s.period === period
    );
  };

  const canProceed = () => {
    switch (currentStep) {
      case 0:
        return formData.selected_services.length > 0;
      case 1:
        return formData.selected_slots.length > 0;
      case 2:
        return formData.client_name && formData.client_email && formData.client_phone && formData.client_address;
      default:
        return true;
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const pricing = calculatePricing();
      const companyId = config?.company?.id;
      
      await widgetApi.subscribe(companyId, {
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        client_address: formData.client_address,
        urssaf_email: formData.urssaf_email,
        selected_services: formData.selected_services,
        other_service_description: formData.other_service_description,
        slot_duration: formData.slot_duration,
        selected_slots: formData.selected_slots,
        needs_description: formData.needs_description,
        notes: formData.notes,
        estimated_monthly: pricing.perVisit,
        estimated_monthly_urssaf: pricing.urssafTotal / pricing.numSlots,
        estimated_annual: pricing.total * 12 / pricing.numSlots
      });

      setSubmitted(true);
      toast.success('Votre demande a été envoyée avec succès !');
    } catch (error) {
      console.error('Submission error:', error);
      const detail = error?.response?.data?.detail;
      let message = 'Une erreur est survenue. Veuillez réessayer.';
      if (typeof detail === 'string' && detail) {
        message = detail;
      } else if (detail && typeof detail === 'object') {
        if (Array.isArray(detail) && detail[0]?.msg) {
          message = detail.map((e) => e.msg).join(' • ');
        } else if (detail.message) {
          message = detail.message;
        }
      } else if (error?.response?.status >= 500) {
        message = 'Erreur serveur, merci de réessayer dans un instant.';
      }
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calendar helpers
  const getDaysInMonth = () => {
    const start = startOfWeek(startOfMonth(currentMonth), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentMonth), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  };

  const getClosureReason = (date) => {
    if (!closures || closures.length === 0) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const iso = `${y}-${m}-${d}`;
    const hit = closures.find((c) => {
      const start = c.start_date;
      const end = c.end_date || start;
      if (!start) return false;
      return iso >= start && iso <= end;
    });
    if (!hit) return null;
    if (hit.type === 'french_holiday') return hit.reason || 'Jour férié';
    return hit.reason || 'Fermeture';
  };

  const isDateAvailable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!(isAfter(date, today) || isSameDay(date, today))) return false;
    return getClosureReason(date) === null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: bgColor }}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: primaryColor }} />
          <p className="mt-4 text-gray-600">Chargement du formulaire...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: bgColor }}>
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${primaryColor}20` }}>
              <Check className="w-8 h-8" style={{ color: primaryColor }} />
            </div>
            <h2 className="text-2xl font-bold mb-2">{widgetConfig?.confirmation_title || 'Demande envoyée !'}</h2>
            <p className="text-gray-600">
              {widgetConfig?.confirmation_message || `Merci ${formData.client_name} ! Votre demande d'abonnement a été transmise à ${config?.company?.name}. Vous recevrez une confirmation par email.`}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const pricing = calculatePricing();

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: bgColor }}>
      <div className="max-w-2xl mx-auto">
        {/* Header with company info */}
        <div className="text-center mb-8">
          {config?.company?.logo_url && (
            <div
              className="w-24 h-24 mx-auto mb-4 rounded-full bg-white flex items-center justify-center overflow-hidden p-2 border-2 transition-all duration-300 hover:scale-105"
              style={{ borderColor: primaryColor, boxShadow: `0 4px 20px ${primaryColor}33` }}
            >
              <img 
                src={getImageUrl(config.company.logo_url)}
                alt={config?.company?.name}
                className="max-w-full max-h-full object-contain"
              />
            </div>
          )}
          <h1 className="text-2xl font-bold" style={{ color: textColor }}>{widgetConfig?.name || config?.company?.name}</h1>
          <p className="text-gray-600">{widgetConfig?.description || widgetConfig?.intro_text || 'Demande d\'abonnement pour des prestations régulières'}</p>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-center mb-8 gap-2">
          {STEPS.map((step, idx) => (
            <div key={step.id} className="flex items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                  idx <= currentStep ? 'text-white' : 'bg-gray-200 text-gray-500'
                }`}
                style={{ backgroundColor: idx <= currentStep ? primaryColor : undefined }}
              >
                {idx < currentStep ? <Check className="w-5 h-5" /> : <step.icon className="w-5 h-5" />}
              </div>
              {idx < STEPS.length - 1 && (
                <div className={`w-12 h-1 mx-1 ${idx < currentStep ? '' : 'bg-gray-200'}`} 
                     style={{ backgroundColor: idx < currentStep ? primaryColor : undefined }} />
              )}
            </div>
          ))}
        </div>

        {/* Form content */}
        <Card>
          <CardHeader>
            <CardTitle>{STEPS[currentStep].title}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 0: Services */}
            {currentStep === 0 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Sélectionnez les prestations souhaitées</p>
                <div className="grid grid-cols-2 gap-3">
                  {/* Use company services from config, fallback to default if none */}
                  {(config?.services && config.services.length > 0 ? config.services : DEFAULT_SERVICES).map(service => (
                    <button
                      key={service.id}
                      onClick={() => handleServiceToggle(service.id)}
                      className={`p-3 rounded-lg border text-left transition-colors ${
                        formData.selected_services.includes(service.id)
                          ? 'border-2'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={formData.selected_services.includes(service.id) ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                    >
                      <span className="text-lg mr-2">{service.icon || '📋'}</span>
                      <span className="text-sm">{service.name || service.label}</span>
                    </button>
                  ))}
                </div>
                {formData.selected_services.includes('autre') && (
                  <Textarea
                    placeholder="Décrivez les autres prestations souhaitées..."
                    value={formData.other_service_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, other_service_description: e.target.value }))}
                  />
                )}
                <div className="space-y-2">
                  <Label>Durée des interventions</Label>
                  <RadioGroup value={formData.slot_duration} onValueChange={(v) => setFormData(prev => ({ ...prev, slot_duration: v }))}>
                    {DURATION_OPTIONS.map(opt => (
                      <div key={opt.value} className="flex items-center space-x-2">
                        <RadioGroupItem value={opt.value} id={opt.value} />
                        <Label htmlFor={opt.value}>{opt.label} ({opt.hours}h)</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            )}

            {/* Step 1: Planning */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">Sélectionnez vos créneaux souhaités (minimum 1)</p>
                
                <div className="flex items-center justify-between mb-4">
                  <Button variant="outline" size="icon" onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="font-medium capitalize">
                    {format(currentMonth, 'MMMM yyyy', { locale: fr })}
                  </span>
                  <Button variant="outline" size="icon" onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-7 gap-1 text-center text-sm">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                    <div key={d} className="font-medium py-2 text-gray-500">{d}</div>
                  ))}
                  {getDaysInMonth().map((day, idx) => {
                    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
                    const isAvailable = isDateAvailable(day);
                    const closureReason = getClosureReason(day);
                    const morningSelected = isSlotSelected(day, 'morning');
                    const afternoonSelected = isSlotSelected(day, 'afternoon');
                    const anySelected = morningSelected || afternoonSelected;

                    return (
                      <div
                        key={idx}
                        className={`p-1 ${!isCurrentMonth ? 'opacity-30' : ''}`}
                        title={closureReason || undefined}
                      >
                        <div
                          className={`rounded-lg p-1 ${closureReason ? 'bg-amber-50' : ''}`}
                          style={anySelected ? { backgroundColor: `${primaryColor}10` } : {}}
                        >
                          <div className="text-xs mb-1 flex items-center justify-center gap-1">
                            <span>{format(day, 'd')}</span>
                            {closureReason && (
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                            )}
                          </div>
                          {isCurrentMonth && !isAvailable && closureReason && (
                            <div className="text-[10px] text-amber-700 truncate text-center leading-tight">
                              {closureReason}
                            </div>
                          )}
                          {isCurrentMonth && isAvailable && (
                            <div className="space-y-1">
                              <button
                                onClick={() => handleSlotToggle(day, 'morning')}
                                className={`w-full text-xs py-0.5 rounded transition-colors ${
                                  morningSelected ? 'text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                                style={morningSelected ? { backgroundColor: primaryColor } : {}}
                              >
                                AM
                              </button>
                              <button
                                onClick={() => handleSlotToggle(day, 'afternoon')}
                                className={`w-full text-xs py-0.5 rounded transition-colors ${
                                  afternoonSelected ? 'text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                }`}
                                style={afternoonSelected ? { backgroundColor: primaryColor } : {}}
                              >
                                PM
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {formData.selected_slots.length > 0 && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: `${primaryColor}10` }}>
                    <p className="font-medium mb-2">{formData.selected_slots.length} créneau(x) sélectionné(s)</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.selected_slots.map((slot, idx) => {
                        const periodLabel = slot.period === 'morning'
                          ? 'Matin'
                          : slot.period === 'afternoon'
                            ? 'Après-midi'
                            : 'Journée';
                        return (
                          <Badge key={idx} variant="secondary" data-testid={`subscription-selected-slot-${slot.date}-${slot.period}`}>
                            {format(new Date(slot.date), 'dd/MM')} - {periodLabel}
                            <button onClick={() => handleSlotToggle(new Date(slot.date), slot.period)} className="ml-1">
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Contact */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Nom complet *</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_email: e.target.value }))}
                      placeholder="jean@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Téléphone *</Label>
                    <Input
                      value={formData.client_phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, client_phone: e.target.value }))}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email URSSAF (optionnel)</Label>
                    <Input
                      type="email"
                      value={formData.urssaf_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, urssaf_email: e.target.value }))}
                      placeholder="Pour l'avance immédiate"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Adresse du lieu d'intervention *</Label>
                  <Textarea
                    value={formData.client_address}
                    onChange={(e) => setFormData(prev => ({ ...prev, client_address: e.target.value }))}
                    placeholder="123 rue de la Paix, 75001 Paris"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Summary */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="p-4 rounded-lg bg-gray-50">
                  <h3 className="font-medium mb-3">Récapitulatif de votre demande</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Services</span>
                      <span>{formData.selected_services.map(s => {
                        const services = config?.services && config.services.length > 0 ? config.services : DEFAULT_SERVICES;
                        const service = services.find(d => d.id === s);
                        return service?.name || service?.label || s;
                      }).join(', ')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Durée</span>
                      <span>{formData.slot_duration === 'full_day' ? 'Journée (7h)' : 'Demi-journée (3h30)'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Créneaux</span>
                      <span>{pricing.numSlots} passage(s)</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg" style={{ backgroundColor: `${primaryColor}10` }}>
                  <h3 className="font-medium mb-3">Estimation tarifaire</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between text-lg">
                      <span>Par passage</span>
                      <span className="font-bold">{pricing.perVisit.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600">
                      <span>Avec avance immédiate URSSAF (-50%)</span>
                      <span>{(pricing.perVisit / 2).toFixed(2)}€</span>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-lg bg-gray-50">
                  <h3 className="font-medium mb-3">Vos coordonnées</h3>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-600">Nom:</span> {formData.client_name}</p>
                    <p><span className="text-gray-600">Email:</span> {formData.client_email}</p>
                    <p><span className="text-gray-600">Téléphone:</span> {formData.client_phone}</p>
                    <p><span className="text-gray-600">Adresse:</span> {formData.client_address}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div className="flex justify-between pt-4">
              {currentStep > 0 ? (
                <Button variant="outline" onClick={() => setCurrentStep(prev => prev - 1)}>
                  <ChevronLeft className="w-4 h-4 mr-2" /> Retour
                </Button>
              ) : (
                <div />
              )}
              
              {currentStep < STEPS.length - 1 ? (
                <Button
                  onClick={() => setCurrentStep(prev => prev + 1)}
                  disabled={!canProceed()}
                  style={{ backgroundColor: primaryColor }}
                >
                  Suivant <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  style={{ backgroundColor: primaryColor }}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Envoi...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> {widgetConfig?.submit_button_text || 'Envoyer la demande'}
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer - Propulsé par ResaWork + Widget ID (hidden for Premium subscribers) */}
        <WidgetFooter widgetId={widgetId} isPremium={config?.is_premium} />
      </div>
    </div>
  );
}
